import React from 'react';
import PropTypes from 'prop-types';
import LoadingView from '../../../shared/components/LoadingView';
import useOrganizationAiSettings from '../../hooks/useOrganizationAiSettings.js';

const PROMPT_MAX = 4000;

// Per-persona copy lives here rather than at the call site so the panel stays a single
// component driven by `persona` — the two prompts differ only in wording and which setter they
// write to, and splitting them into separate components duplicated the editor three times over.
const PERSONAS = {
  field_engineer: {
    id: 'fe-ai-prompt',
    label: 'Field engineer AI system prompt',
    description:
      'Optional instructions appended to the AI chat system prompt for field engineers on your assets. '
      + 'Use this for organization-specific safety rules, terminology, or escalation guidance.',
    placeholder: 'Example: Always remind engineers to verify lockout/tagout before electrical work.',
    valueKey: 'fieldEngineerPrompt',
    setterKey: 'setFieldEngineerPrompt',
  },
  workflow: {
    id: 'workflow-ai-prompt',
    label: 'Workflow creation AI prompt',
    description:
      'Optional instructions for the workflow AI editor when admins build workflows. Use this for '
      + 'terminology, branching preferences, required safety steps, or industry-specific guidance.',
    placeholder:
      'Example: Always include a lockout/tagout verification step before electrical work. '
      + 'Prefer short instruction steps with clear action buttons.',
    valueKey: 'workflowPrompt',
    setterKey: 'setWorkflowPrompt',
    footnote: 'Organization knowledge files for workflow creation are planned for a future release.',
  },
};

export default function AiSettingsPanel({ persona }) {
  const settings = useOrganizationAiSettings();
  const config = PERSONAS[persona];

  if (settings.loading) {
    return (
      <LoadingView
        symbolClass="bi-robot"
        title="Loading AI settings"
        subtitle="Fetching organization AI configuration."
        className="py-5"
      />
    );
  }

  const value = settings[config.valueKey] || '';
  const onChange = settings[config.setterKey];

  return (
    <>
      {settings.error && (
        <div className="alert alert-danger mb-3" role="alert">
          <i className="bi bi-exclamation-triangle me-2" />
          {settings.error}
        </div>
      )}
      {settings.saveMessage && (
        <div className="alert alert-success py-2 mb-3" role="status">
          {settings.saveMessage}
        </div>
      )}

      <section>
        <div className="mb-4">
          <h6 className="text-muted fw-normal mb-1">{config.label}</h6>
          <p className="text-muted small mb-0">{config.description}</p>
        </div>

        <div className="mb-2 d-flex justify-content-between align-items-center">
          <label htmlFor={config.id} className="form-label mb-0 fw-semibold">
            Custom prompt
          </label>
          <span className="small text-muted">
            {value.length} / {PROMPT_MAX}
          </span>
        </div>
        <textarea
          id={config.id}
          className="form-control font-monospace small"
          rows={14}
          maxLength={PROMPT_MAX}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={config.placeholder}
        />

        <div className="mt-3">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => settings.save(persona)}
            disabled={settings.saving}
          >
            {settings.saving ? 'Saving…' : 'Save'}
          </button>
        </div>

        {config.footnote && <p className="text-muted small mt-3 mb-0">{config.footnote}</p>}
      </section>
    </>
  );
}

AiSettingsPanel.propTypes = {
  persona: PropTypes.oneOf(Object.keys(PERSONAS)).isRequired,
};
